<template>
    <div class="user-management-container">
        <!-- Header Section -->
        <el-card class="header-card" shadow="never">
            <el-row :gutter="20" align="middle">
                <el-col :span="24">
                    <h2 style="margin: 0; color: #303133;">Manajemen Pengguna</h2>
                    <p style="margin: 5px 0 0 0; color: #909399; font-size: 14px;">Kelola pengguna sistem dan hak aksesnya</p>
                </el-col>
            </el-row>
        </el-card>

        <!-- Search Section -->
        <el-card class="search-card" shadow="never">
            <el-row :gutter="20" align="middle">
                <el-col :md="8">
                    <el-input 
                        v-model="query" 
                        placeholder="Cari berdasarkan nama atau email..." 
                        clearable
                        @clear="fetchData"
                        @keyup.enter="filterData"
                        :prefix-icon="Search"
                    />
                </el-col>
                <el-col :md="4">
                    <el-button @click="filterData" :icon="Search" :loading="loading">
                        Cari
                    </el-button>
                </el-col>
                <el-col :md="12" style="text-align: right;">
                    <el-button 
                        type="primary" 
                        @click="showForm()"
                        style="background-color: #409EFF; border-color: #409EFF; color: white;"
                    >
                        <el-icon style="margin-right: 5px;"><Plus /></el-icon>
                        Tambah
                    </el-button>
                </el-col>
            </el-row>
        </el-card>

        <!-- Table Section -->
        <el-card shadow="never">
            <el-table 
                :data="tableData" 
                v-loading="loading"
                empty-text="Tidak ada data pengguna"
                style="width: 100%"
                :stripe="true"
            >
                <el-table-column type="index" width="60" label="No" align="center" />
                <el-table-column prop="name" label="Nama" min-width="150" show-overflow-tooltip />
                <el-table-column prop="email" label="Email" min-width="200" show-overflow-tooltip />
                <el-table-column prop="kode_unit" label="Akses Unit" width="150">
                    <template #default="scope">
                        <el-tag v-if="scope.row.kode_unit" type="info" size="small">
                            {{ getUnitName(scope.row.kode_unit) }}
                        </el-tag>
                        <span v-else style="color: #909399; font-size: 12px;">Belum ada unit</span>
                    </template>
                </el-table-column>
                <el-table-column prop="permissions" label="Hak Akses" min-width="250">
                    <template #default="scope">
                        <div v-if="scope.row.permissions && scope.row.permissions.length > 0">
                            <el-tag 
                                v-for="item in scope.row.permissions" 
                                :key="item.role_id"
                                size="small"
                                style="margin: 2px;"
                            >
                                {{ item.module_name }} - {{ item.role_name }}
                            </el-tag>
                        </div>
                        <span v-else style="color: #909399; font-size: 12px;">Belum ada hak akses</span>
                    </template>
                </el-table-column>
                
                <el-table-column fixed="right" label="Aksi" width="200" align="center">
                    <template #default="scope">
                        <div style="display: flex; gap: 8px; justify-content: center;">
                            <el-tooltip content="Ubah Data" placement="top">
                                <el-button 
                                    size="small" 
                                    type="primary" 
                                    style="background-color: #409EFF; border-color: #409EFF; color: white;"
                                    @click="showUbah(scope)"
                                >
                                    <el-icon><Edit /></el-icon>
                                </el-button>
                            </el-tooltip>
                            <el-tooltip content="Ubah Kata Sandi" placement="top">
                                <el-button 
                                    size="small" 
                                    type="warning"
                                    style="background-color: #E6A23C; border-color: #E6A23C; color: white;"
                                    @click="showSandi(scope)"
                                >
                                    <el-icon><Key /></el-icon>
                                </el-button>
                            </el-tooltip>
                            <el-tooltip content="Hapus Pengguna" placement="top">
                                <el-popconfirm 
                                    title="Apakah Anda yakin ingin menghapus pengguna ini?" 
                                    confirm-button-text="Ya"
                                    cancel-button-text="Tidak"
                                    @confirm="hapus(scope)"
                                >
                                    <template #reference>
                                        <el-button 
                                            size="small" 
                                            type="danger"
                                            style="background-color: #F56C6C; border-color: #F56C6C; color: white;"
                                        >
                                            <el-icon><Delete /></el-icon>
                                        </el-button>
                                    </template>
                                </el-popconfirm>
                            </el-tooltip>
                        </div>
                    </template>
                </el-table-column>
            </el-table>
            
            <!-- Pagination -->
            <div style="margin-top: 20px; display: flex; justify-content: center;">
                <el-pagination 
                    v-model:current-page="currentPage" 
                    v-model:page-size="pageSize" 
                    :page-sizes="[10, 20, 50, 100]"
                    :layout="paginationLayout"
                    :total="tableCount" 
                    @size-change="handleSizeChange" 
                    @current-change="handleCurrentChange"
                    :background="true"
                />
            </div>
        </el-card>

        <!-- User Form Dialog -->
        <el-dialog 
            v-model="dialogFormVisible" 
            :title="form.id ? 'Ubah Data Pengguna' : 'Tambah Pengguna Baru'" 
            width="600px"
            :close-on-click-modal="false"
        >
            <el-form 
                :model="form" 
                label-width="140px" 
                :rules="rules" 
                ref="ruleForm"
                label-position="right"
            >
                <el-form-item label="Nama Lengkap" prop="name">
                    <el-input 
                        v-model="form.name" 
                        placeholder="Masukkan nama lengkap pengguna"
                        :prefix-icon="User"
                    />
                </el-form-item>
                <el-form-item label="Alamat Email" prop="email">
                    <el-input 
                        v-model="form.email" 
                        placeholder="Masukkan alamat email"
                        :prefix-icon="Message"
                    />
                </el-form-item>
                <el-form-item v-if="!form.id" label="Kata Sandi" prop="password">
                    <el-input 
                        type="password" 
                        v-model="form.password" 
                        placeholder="Masukkan kata sandi"
                        show-password
                        :prefix-icon="Lock"
                    />
                </el-form-item>
                <el-form-item label="Akses Fitur" prop="role_id">
                    <el-select 
                        v-model="form.role_id" 
                        multiple 
                        placeholder="Pilih hak akses pengguna"
                        style="width: 100%"
                        :loading="loadingRoles"
                    >
                        <el-option-group 
                            v-for="(group, module) in groupedRoles" 
                            :key="module"
                            :label="module"
                        >
                            <el-option 
                                v-for="item in group" 
                                :key="item.id" 
                                :label="item.role_name" 
                                :value="item.id"
                            >
                                <span style="float: left">{{ item.role_name }}</span>
                                <span style="float: right; color: #8492a6; font-size: 13px">{{ item.module_name }}</span>
                            </el-option>
                        </el-option-group>
                    </el-select>
                </el-form-item>
                <el-form-item v-show="false" label="Akses Data" prop="kode_unit">
                    <el-select 
                        v-model="form.kode_unit" 
                        placeholder="Pilih unit organisasi"
                        style="width: 100%"
                        filterable
                        :loading="loadingOrg"
                    >
                        <el-option 
                            v-for="item in list_org" 
                            :key="item.kode_unit" 
                            :label="item.unit" 
                            :value="item.kode_unit"
                        />
                    </el-select>
                </el-form-item>
            </el-form>
            <template #footer>
                <span class="dialog-footer">
                    <el-button @click="dialogFormVisible = false">Batal</el-button>
                    <el-button 
                        type="primary" 
                        @click="form.id ? ubah() : tambah()"
                        :loading="submitting"
                    >
                        {{ form.id ? 'Simpan' : 'Tambah' }}
                    </el-button>
                </span>
            </template>
        </el-dialog>

        <!-- Password Dialog -->
        <el-dialog 
            v-model="dialogPasswordVisible" 
            title="Ubah Kata Sandi" 
            width="500px"
            :close-on-click-modal="false"
        >
            <el-alert 
                title="Ketentuan Kata Sandi"
                type="info"
                :closable="false"
                show-icon
                style="margin-bottom: 20px;"
            >
                Kata sandi minimal terdiri dari 8 karakter
            </el-alert>
            <el-form 
                :model="form" 
                label-width="160px" 
                :rules="ruleFormPassword" 
                ref="ruleFormPassword"
            >
                <el-form-item label="Kata Sandi Baru" prop="password">
                    <el-input 
                        type="password" 
                        v-model="form.password" 
                        placeholder="Masukkan kata sandi baru"
                        show-password
                        :prefix-icon="Lock"
                    />
                </el-form-item>
                <el-form-item label="Konfirmasi Kata Sandi" prop="confirm_password">
                    <el-input 
                        type="password" 
                        v-model="form.confirm_password" 
                        placeholder="Konfirmasi kata sandi baru"
                        show-password
                        :prefix-icon="Lock"
                    />
                </el-form-item>
            </el-form>
            <template #footer>
                <span class="dialog-footer">
                    <el-button @click="dialogPasswordVisible = false">Batal</el-button>
                    <el-button 
                        type="primary" 
                        @click="simpanSandi()"
                        :loading="submitting"
                    >
                        Perbarui Kata Sandi
                    </el-button>
                </span>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { 
    Plus, Search, Edit, Delete, Key, 
    User, Lock, Message, InfoFilled 
} from '@element-plus/icons-vue'

export default {
    name: 'UserView',
    data() {
        const validateEmail = (rule, value, callback) => {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (!emailRegex.test(value)) {
                callback(new Error('Harap masukkan alamat email yang valid'))
            } else {
                callback()
            }
        }
        
        const validatePasswordConfirm = (rule, value, callback) => {
            if (value !== this.form.password) {
                callback(new Error('Konfirmasi kata sandi tidak sesuai'))
            } else {
                callback()
            }
        }
        
        return {
            Plus, Search, Edit, Delete, Key,
            User, Lock, Message, InfoFilled,
            tableData: [],
            tableCount: 0,
            currentPage: 1,
            pageSize: 20,
            dialogFormVisible: false,
            dialogPasswordVisible: false,
            roleList: [],
            list_org: [],
            query: '',
            loading: false,
            loadingRoles: false,
            loadingOrg: false,
            submitting: false,
            form: {
                id: '',
                name: '',
                email: '',
                password: '',
                confirm_password: '',
                role_id: [],
                kode_unit: ''
            },
            rules: {
                name: [
                    { required: true, message: 'Harap masukkan nama lengkap pengguna', trigger: 'blur' },
                    { min: 3, message: 'Nama minimal terdiri dari 3 karakter', trigger: 'blur' }
                ],
                email: [
                    { required: true, message: 'Harap masukkan alamat email', trigger: 'blur' },
                    { validator: validateEmail, trigger: 'blur' }
                ],
                password: [
                    { required: true, message: 'Harap masukkan kata sandi', trigger: 'blur' },
                    { min: 8, message: 'Kata sandi minimal terdiri dari 8 karakter', trigger: 'blur' }
                ],
                role_id: [
                    { required: true, message: 'Harap pilih minimal satu hak akses', trigger: 'change', type: 'array' }
                ],/*
                kode_unit: [
                    { required: true, message: 'Harap pilih unit organisasi', trigger: 'change' }
                ]*/
            },
            ruleFormPassword: {
                password: [
                    { required: true, message: 'Harap masukkan kata sandi baru', trigger: 'blur' },
                    { min: 8, message: 'Kata sandi minimal terdiri dari 8 karakter', trigger: 'blur' }
                ],
                confirm_password: [
                    { required: true, message: 'Harap konfirmasi kata sandi', trigger: 'blur' },
                    { validator: validatePasswordConfirm, trigger: 'blur' }
                ]
            }
        }
    },
    computed: {
        apiHeaders() {
            return {
                'Authorization': `Bearer ${this.$store.state.auth_token}`
            }
        },
        baseUrl() {
            return this.$store.state.base_url
        },
        groupedRoles() {
            const grouped = {}
            this.roleList.forEach(role => {
                if (!grouped[role.module_name]) {
                    grouped[role.module_name] = []
                }
                grouped[role.module_name].push(role)
            })
            return grouped
        },
        paginationLayout() {
            return window.innerWidth < 768 
                ? 'prev, pager, next' 
                : 'total, sizes, prev, pager, next, jumper'
        }
    },
    mounted() {
        this.fetchData()
        this.getRole()
        this.fetchDataOrg()
    },
    methods: {
        filterData() {
            this.fetchData()
        },
        handleCurrentChange() {
            this.fetchData()
        },
        handleSizeChange() {
            this.currentPage = 1
            this.fetchData()
        },
        async fetchData() {
            this.loading = true
            try {
                const params = {
                    limit: this.pageSize,
                    offset: (this.currentPage - 1) * this.pageSize,
                    query: this.query
                }

                const response = await axios.get(`${this.baseUrl}/api/user`, {
                    params,
                    headers: this.apiHeaders
                })
                
                this.tableData = response.data.data
                this.tableCount = response.data.count
            } catch (error) {
                this.handleError(error, 'Gagal mengambil data pengguna')
            } finally {
                this.loading = false
            }
        },
        async fetchDataOrg() {
            this.loadingOrg = true
            try {
                const params = {
                    limit: 100,
                    table_name: "spf_org",
                    fields: 'kode_unit,unit'
                }

                const response = await axios.get(`${this.baseUrl}/api/data`, {
                    params,
                    headers: this.apiHeaders
                })
                
                this.list_org = response.data.data
            } catch (error) {
                this.handleError(error, 'Gagal mengambil data organisasi')
            } finally {
                this.loadingOrg = false
            }
        },
        showForm() {
            this.resetForm()
            this.dialogFormVisible = true
        },
        showUbah(selected) {
            this.resetForm()
            const { id, name, email, permissions, kode_unit } = selected.row
            
            this.form.id = id
            this.form.name = name
            this.form.email = email
            this.form.kode_unit = kode_unit || ''
            this.form.role_id = permissions.map(p => Number(p.role_id))
            
            this.dialogFormVisible = true
        },
        showSandi(selected) {
            this.resetForm()
            this.form.id = selected.row.id
            this.dialogPasswordVisible = true
        },
        async tambah() {
            this.rules.password[0].required = true
            
            try {
                const valid = await this.$refs['ruleForm'].validate()
                if (!valid) return
                
                this.submitting = true
                const postData = {
                    name: this.form.name,
                    email: this.form.email,
                    password: this.form.password,
                    role_id: this.form.role_id,
                    kode_unit: this.form.kode_unit
                }
                
                await axios.post(`${this.baseUrl}/api/user`, postData, {
                    headers: this.apiHeaders
                })
                
                ElMessage({
                    message: 'Pengguna berhasil ditambahkan!',
                    type: 'success'
                })
                this.dialogFormVisible = false
                this.fetchData()
            } catch (error) {
                this.handleError(error, 'Gagal menambahkan pengguna')
            } finally {
                this.submitting = false
            }
        },
        async ubah() {
            this.rules.password[0].required = false
            
            try {
                const valid = await this.$refs['ruleForm'].validate()
                if (!valid) return
                
                this.submitting = true
                const postData = {
                    name: this.form.name,
                    email: this.form.email,
                    role_id: this.form.role_id,
                    kode_unit: this.form.kode_unit
                }
                
                await axios.put(`${this.baseUrl}/api/user/${this.form.id}`, postData, {
                    headers: this.apiHeaders
                })
                
                this.dialogFormVisible = false
                ElMessage({
                    message: 'Data pengguna berhasil diperbarui!',
                    type: 'success'
                })
                this.fetchData()
            } catch (error) {
                this.handleError(error)
            } finally {
                this.submitting = false
            }
        },
        async simpanSandi() {
            try {
                const valid = await this.$refs['ruleFormPassword'].validate()
                if (!valid) return
                
                this.submitting = true
                const postData = {
                    password: this.form.password,
                    confirm_password: this.form.confirm_password
                }
                
                await axios.put(`${this.baseUrl}/api/user-password/${this.form.id}`, postData, {
                    headers: this.apiHeaders
                })
                
                this.dialogPasswordVisible = false
                ElMessage({
                    message: 'Kata sandi berhasil diperbarui!',
                    type: 'success'
                })
                this.fetchData()
            } catch (error) {
                this.handleError(error)
            } finally {
                this.submitting = false
            }
        },
        async hapus(selected) {
            this.loading = true
            try {
                await axios.delete(`${this.baseUrl}/api/user/${selected.row.id}`, {
                    headers: this.apiHeaders
                })
                
                ElMessage({
                    message: 'Pengguna berhasil dihapus!',
                    type: 'success'
                })
                this.fetchData()
            } catch (error) {
                this.handleError(error)
            } finally {
                this.loading = false
            }
        },
        async getRole() {
            this.loadingRoles = true
            try {
                const response = await axios.get(`${this.baseUrl}/api/roles`, {
                    headers: this.apiHeaders
                })
                
                this.roleList = response.data.data
            } catch (error) {
                this.handleError(error, 'Gagal mengambil data hak akses')
            } finally {
                this.loadingRoles = false
            }
        },
        resetSearch() {
            this.query = ''
            this.currentPage = 1
            this.fetchData()
        },
        getUnitName(kodeUnit) {
            const unit = this.list_org.find(u => u.kode_unit === kodeUnit)
            return unit ? unit.unit : kodeUnit
        },
        resetForm() {
            this.form = {
                id: '',
                name: '',
                email: '',
                password: '',
                confirm_password: '',
                role_id: [],
                kode_unit: ''
            }
        },
        handleError(error, defaultMessage = '') {
            const message = error.response?.data?.message || defaultMessage || 'Terjadi kesalahan'
            ElMessage({
                message,
                type: 'error'
            })
            console.error('Error:', error)
        }
    }
}
</script>

<style scoped>
.user-management-container {
    padding: 20px;
}

.header-card {
    margin-bottom: 20px;
}

.search-card {
    margin-bottom: 20px;
}

.el-tag {
    margin: 2px;
}

@media (max-width: 768px) {
    .user-management-container {
        padding: 10px;
    }
    
    .el-dialog {
        width: 90% !important;
    }
}
</style>